/**
 * A2A Agent Chat Client
 *
 * Implementation based on the A2A protocol specification.
 * First fetches the agent-card.json to discover the agent's capabilities and endpoint,
 * then uses JSON-RPC 2.0 over SSE for messaging.
 *
 * Key concepts:
 * - contextId (aka runId): Identifies a conversation/run. Each context can have multiple tasks.
 * - taskId: Identifies a single message exchange within a context.
 *
 * Flow:
 * 1. New Chat: Send message WITHOUT contextId -> server creates new context and returns contextId
 * 2. Continue Chat: Send message WITH contextId -> creates new task in existing context
 */

import { isLocalMode, getLocalRuntimeUrl } from './localRuntimeClient';
import { telemetryService } from '@/services/telemetryService';

export interface AgentConfig {
  agentUrl: string;
  apiKey?: string | undefined;
  developerKey?: string | undefined;
}

export interface AgentCard {
  name: string;
  description?: string;
  url: string;
  version?: string;
  capabilities?: {
    streaming?: boolean;
    pushNotifications?: boolean;
    stateTransitionHistory?: boolean;
  };
  defaultInputModes?: string[];
  defaultOutputModes?: string[];
  skills?: Array<{
    id: string;
    name: string;
    description?: string;
  }>;
}

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  contextId?: string | undefined;
  isStreaming?: boolean | undefined;
}

export interface ChatSession {
  id: string;
  name?: string;
  createdAt: Date;
  updatedAt: Date;
  lastMessage?: string;
}

export interface StreamUpdate {
  content: string;
  isComplete: boolean;
  contextId?: string | undefined;
  taskId?: string | undefined;
  /** True if this is a new message (not appending to previous content) */
  isNewMessage?: boolean | undefined;
  /** Task state from A2A protocol (e.g., 'completed', 'failed') */
  taskState?: string | undefined;
}

type StreamCallback = (update: StreamUpdate) => void;
type ErrorCallback = (error: Error) => void;

/**
 * SSE Client for streaming responses
 */
class SSEStreamClient {
  private reader: ReadableStreamDefaultReader<Uint8Array> | null = null;
  private abortController: AbortController | null = null;
  private closed = false;

  constructor(
    private url: string,
    private options: {
      headers: Record<string, string>;
      body: string;
      onMessage: (data: unknown) => void;
      onError: (error: Error) => void;
      onComplete: () => void;
    }
  ) {
    this.connect();
  }

  private async connect(): Promise<void> {
    if (this.closed) return;

    this.abortController = new AbortController();

    try {
      const response = await fetch(this.url, {
        method: 'POST',
        headers: {
          ...this.options.headers,
          Accept: 'text/event-stream',
        },
        body: this.options.body,
        signal: this.abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      if (!response.body) {
        throw new Error('No response body');
      }

      this.reader = response.body.getReader();
      telemetryService.trackEvent('sse.agent_chat.connected');
      const decoder = new TextDecoder();
      let buffer = '';

      while (this.reader && !this.closed) {
        const { done, value } = await this.reader.read();

        if (done) {
          telemetryService.trackEvent('sse.agent_chat.disconnected', { reason: 'stream_complete' });
          this.options.onComplete();
          break;
        }

        buffer += decoder.decode(value, { stream: true });

        // Process complete SSE messages
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        let currentData: string | null = null;

        for (const line of lines) {
          if (line.trim() === '') {
            // Empty line indicates end of message
            if (currentData !== null) {
              try {
                const parsed = JSON.parse(currentData);
                this.options.onMessage(parsed);
              } catch {
                // Not JSON, skip
              }
              currentData = null;
            }
          } else if (line.startsWith('data:')) {
            currentData = line.slice(5).trim();
          }
        }
      }
    } catch (error) {
      if (!this.closed && (error as Error).name !== 'AbortError') {
        telemetryService.trackEvent('sse.agent_chat.error', { error: (error as Error).message });
        telemetryService.trackEvent('sse.agent_chat.disconnected', { reason: 'error' });
        this.options.onError(error as Error);
      } else {
        telemetryService.trackEvent('sse.agent_chat.disconnected', { reason: this.closed ? 'closed' : 'aborted' });
      }
    }
  }

  close(): void {
    this.closed = true;
    this.abortController?.abort();
    this.reader?.cancel();
    this.reader = null;
  }
}

/**
 * Agent Chat Client
 *
 * Connects to an A2A agent workflow and handles message streaming.
 * Follows the A2A protocol:
 * 1. Fetches agent-card.json from {agentUrl}/.well-known/agent-card.json
 * 2. Uses the endpoint URL from the agent card for messaging
 */
export class AgentChatClient {
  private config: AgentConfig;
  private agentCard: AgentCard | null = null;
  private endpointUrl: string | null = null;
  private currentContextId: string | null = null;
  private activeStream: SSEStreamClient | null = null;
  private initPromise: Promise<void> | null = null;

  constructor(config: AgentConfig) {
    this.config = config;
  }

  /**
   * Build request headers with authentication
   */
  private buildHeaders(): Record<string, string> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    if (this.config.apiKey) {
      headers['X-API-Key'] = this.config.apiKey;
    }

    if (this.config.developerKey) {
      headers['x-ms-obo-userToken'] = `Key ${this.config.developerKey}`;
    }

    return headers;
  }

  /**
   * Fetch the agent card to discover the agent's endpoint and capabilities
   */
  private async fetchAgentCard(): Promise<AgentCard> {
    // Build the agent card URL - append /.well-known/agent-card.json to the agent URL
    // Note: Strip /v1 suffix if present because local mode agentUrl includes /v1 for A2A messaging,
    // but the agent card endpoint doesn't have a versioned route. Cloud mode doesn't include /v1.
    // TODO: Consider removing /v1 from agentUrlService.ts local mode and adding it back only for A2A messaging calls.
    const baseUrl = this.config.agentUrl.replace(/\/v\d+$/, '');
    const agentCardUrl = `${baseUrl}/.well-known/agent-card.json`;

    const response = await fetch(agentCardUrl, {
      method: 'GET',
      headers: this.buildHeaders(),
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch agent card: ${response.status}`);
    }

    const agentCard = await response.json();
    return agentCard as AgentCard;
  }

  /**
   * Initialize the client by fetching the agent card
   */
  async initialize(): Promise<void> {
    if (this.initPromise) {
      return this.initPromise;
    }

    this.initPromise = (async () => {
      try {
        this.agentCard = await this.fetchAgentCard();
        // The agent card's url field contains the actual endpoint for messaging
        // In local mode, convert absolute URL to relative URL to use Vite proxy and avoid CORS issues
        if (isLocalMode() && this.agentCard.url) {
          const localRuntimeUrl = getLocalRuntimeUrl();
          if (this.agentCard.url.startsWith(localRuntimeUrl)) {
            // Convert http://localhost:7071/api/... to /api/...
            this.endpointUrl = this.agentCard.url.replace(localRuntimeUrl, '');
          } else {
            this.endpointUrl = this.agentCard.url;
          }
        } else {
          this.endpointUrl = this.agentCard.url;
        }
      } catch (error) {
        console.error('Failed to initialize agent client:', error);
        // Fall back to using the agentUrl directly if agent card fetch fails
        this.endpointUrl = this.config.agentUrl;
      }
    })();

    return this.initPromise;
  }

  /**
   * Ensure the client is initialized before making requests
   */
  private async ensureInitialized(): Promise<string> {
    if (!this.endpointUrl) {
      await this.initialize();
    }
    return this.endpointUrl || this.config.agentUrl;
  }

  /**
   * Get the current context (session) ID
   */
  getContextId(): string | null {
    return this.currentContextId;
  }

  /**
   * Set the context ID for continuing a previous session
   */
  setContextId(contextId: string): void {
    this.currentContextId = contextId;
  }

  /**
   * Start a new chat session (context)
   *
   * In A2A protocol:
   * - Clearing contextId means the next message will create a NEW context on the server
   * - The server will return a new contextId which will be used for subsequent messages
   * - This effectively starts a new "run" or conversation thread
   */
  startNewSession(): void {
    this.currentContextId = null;
  }

  /**
   * Clear the context ID to start fresh
   * Alias for startNewSession for clarity
   */
  clearContext(): void {
    this.currentContextId = null;
  }

  /**
   * Get the agent card (after initialization)
   */
  getAgentCard(): AgentCard | null {
    return this.agentCard;
  }

  /**
   * Send a message and stream the response
   */
  async sendMessage(
    content: string,
    onUpdate: StreamCallback,
    onError?: ErrorCallback
  ): Promise<void> {
    const endpointUrl = await this.ensureInitialized();

    // Close any existing stream
    this.activeStream?.close();

    const messageId = `msg-${Date.now()}-${Math.random().toString(36).slice(2)}`;

    // Build A2A message format
    const a2aMessage = {
      kind: 'message',
      messageId,
      role: 'user',
      parts: [{ kind: 'text', text: content }],
      ...(this.currentContextId ? { contextId: this.currentContextId } : {}),
    };

    // Build JSON-RPC request
    const jsonRpcRequest = {
      jsonrpc: '2.0',
      method: 'message/stream',
      params: {
        message: a2aMessage,
        configuration: {
          acceptedOutputModes: ['text'],
        },
      },
      id: Date.now(),
    };

    let accumulatedContent = '';
    let taskId: string | null = null;
    let lastMessageId: string | null = null;

    return new Promise((resolve, reject) => {
      this.activeStream = new SSEStreamClient(endpointUrl, {
        headers: this.buildHeaders(),
        body: JSON.stringify(jsonRpcRequest),
        onMessage: (data: unknown) => {
          try {
            const jsonRpcData = data as Record<string, unknown>;

            // Check for JSON-RPC error
            if (jsonRpcData.error) {
              const error = jsonRpcData.error as { message?: string };
              throw new Error(error.message || 'Unknown error');
            }

            const result = (jsonRpcData.result || jsonRpcData) as Record<string, unknown>;

            // Capture context ID from response - always update to latest from server
            // This ensures we track the server-assigned context ID for new sessions
            // and continue using the same context for subsequent messages
            if (result.contextId) {
              this.currentContextId = result.contextId as string;
            }

            // Capture task ID
            if (result.id || result.taskId) {
              taskId = (result.id || result.taskId) as string;
            }

            // Handle different response types
            if (result.kind === 'status-update' || result.status) {
              const status = (result.status || result) as Record<string, unknown>;
              const message = status.message as Record<string, unknown> | undefined;

              if (message?.parts) {
                const parts = message.parts as Array<{ kind: string; text?: string }>;
                const textParts = parts
                  .filter((p) => p.kind === 'text')
                  .map((p) => p.text || '')
                  .join('');

                if (textParts) {
                  // Check for messageId from backend to detect new messages
                  // Each RespondToChat action sends a unique messageId
                  const messageId = message.messageId as string | undefined;
                  const isNewMessage = messageId !== undefined && lastMessageId !== null && messageId !== lastMessageId;
                  
                  if (messageId) {
                    lastMessageId = messageId;
                  }
                  
                  accumulatedContent = textParts;
                  onUpdate({
                    content: accumulatedContent,
                    isComplete: false,
                    contextId: this.currentContextId || undefined,
                    taskId: taskId || undefined,
                    isNewMessage,
                  });
                }
              }

              // Check for completion
              const state = status.state as string | undefined;
              if (state === 'completed' || state === 'failed' || result.final) {
                if (state === 'failed') {
                  console.warn('[agentChatClient] Task failed:', { taskId, contextId: this.currentContextId, state });
                }
                onUpdate({
                  content: accumulatedContent,
                  isComplete: true,
                  contextId: this.currentContextId || undefined,
                  taskId: taskId || undefined,
                  taskState: state,
                });
                this.activeStream?.close();
                resolve();
              }
            } else if (result.kind === 'artifact-update') {
              // Handle streaming artifact updates (text content)
              const artifact = result.artifact as Record<string, unknown> | undefined;
              if (artifact?.parts) {
                const parts = artifact.parts as Array<{ kind: string; text?: string }>;
                const textParts = parts
                  .filter((p) => p.kind === 'text' || p.kind === 'Text')
                  .map((p) => p.text || '')
                  .join('');

                if (textParts) {
                  // Determine if this is a new message (replacing existing content)
                  // A new message occurs when:
                  // 1. append is false (not appending to previous)
                  // 2. We already had content (so this is replacing, not starting fresh)
                  const isNewMessage = !result.append && accumulatedContent.length > 0;
                  
                  console.log('[agentChatClient] artifact-update:', {
                    append: result.append,
                    accumulatedContentLength: accumulatedContent.length,
                    isNewMessage,
                    textParts: textParts.substring(0, 50) + '...',
                  });
                  
                  if (result.append) {
                    accumulatedContent += textParts;
                  } else {
                    accumulatedContent = textParts;
                  }

                  onUpdate({
                    content: accumulatedContent,
                    isComplete: false,
                    contextId: this.currentContextId || undefined,
                    taskId: taskId || undefined,
                    isNewMessage,
                  });
                }
              }
            } else if (result.messages) {
              // Handle legacy format with messages array
              const messages = result.messages as Array<{
                role: string;
                content: Array<{ type: string; content?: string }>;
              }>;

              for (const msg of messages) {
                if (msg.role === 'assistant') {
                  const textContent = msg.content
                    .filter((p) => p.type === 'text')
                    .map((p) => p.content || '')
                    .join('');

                  if (textContent) {
                    accumulatedContent = textContent;
                    onUpdate({
                      content: accumulatedContent,
                      isComplete: false,
                      contextId: this.currentContextId || undefined,
                      taskId: taskId || undefined,
                    });
                  }
                }
              }

              // Check for completion
              const state = result.state as string | undefined;
              if (state === 'completed' || state === 'failed') {
                onUpdate({
                  content: accumulatedContent,
                  isComplete: true,
                  contextId: this.currentContextId || undefined,
                  taskId: taskId || undefined,
                });
                this.activeStream?.close();
                resolve();
              }
            }
          } catch (error) {
            onError?.(error as Error);
          }
        },
        onError: (error) => {
          onError?.(error);
          reject(error);
        },
        onComplete: () => {
          onUpdate({
            content: accumulatedContent,
            isComplete: true,
            contextId: this.currentContextId || undefined,
            taskId: taskId || undefined,
          });
          resolve();
        },
      });
    });
  }

  /**
   * Cancel the current streaming response
   */
  cancelStream(): void {
    this.activeStream?.close();
    this.activeStream = null;
  }

  /**
   * List chat sessions (contexts) from the server
   */
  async listSessions(options?: {
    limit?: number;
    includeArchived?: boolean;
  }): Promise<ChatSession[]> {
    const endpointUrl = await this.ensureInitialized();

    const jsonRpcRequest = {
      jsonrpc: '2.0',
      method: 'contexts/list',
      params: {
        limit: options?.limit ?? 20,
        includeLastTask: true,
        includeArchived: options?.includeArchived ?? false,
      },
      id: `list-${Date.now()}`,
    };

    const response = await fetch(endpointUrl, {
      method: 'POST',
      headers: this.buildHeaders(),
      body: JSON.stringify(jsonRpcRequest),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();

    if (data.error) {
      throw new Error(data.error.message || 'Failed to list sessions');
    }

    const contexts = data.result || [];

    return contexts.map((ctx: Record<string, unknown>) => ({
      id: ctx.id as string,
      name: (ctx.name as string) || (ctx.id as string),
      createdAt: this.parseServerDate(ctx.createdAt as string),
      updatedAt: this.parseServerDate(ctx.updatedAt as string),
      lastMessage: this.extractLastMessage(ctx.lastTask as Record<string, unknown> | undefined),
    }));
  }

  /**
   * Load messages for a specific session
   */
  async loadSessionMessages(contextId: string): Promise<ChatMessage[]> {
    const endpointUrl = await this.ensureInitialized();

    const jsonRpcRequest = {
      jsonrpc: '2.0',
      method: 'tasks/list',
      params: {
        Id: contextId,
      },
      id: `tasks-${Date.now()}`,
    };

    const response = await fetch(endpointUrl, {
      method: 'POST',
      headers: this.buildHeaders(),
      body: JSON.stringify(jsonRpcRequest),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();

    if (data.error) {
      throw new Error(data.error.message || 'Failed to load session messages');
    }

    const tasks = data.result || [];
    const messages: ChatMessage[] = [];

    for (const task of tasks) {
      // Extract messages from task history
      const history = task.history || [];

      for (const msg of history) {
        const textContent = (msg.parts || [])
          .filter((p: { kind: string }) => p.kind === 'text')
          .map((p: { text?: string }) => p.text || '')
          .join('');

        if (textContent) {
          messages.push({
            id: msg.messageId,
            role: msg.role === 'agent' ? 'assistant' : 'user',
            content: textContent,
            timestamp: this.parseServerDate(msg.metadata?.timestamp),
            contextId,
          });
        }
      }
    }

    // Sort by timestamp (oldest first)
    // When timestamps are equal, ensure user messages come before assistant messages
    // This handles the case where backend returns them in reverse order with same timestamp
    // TODO: Fix in backend - the A2A tasks/list API returns history with agent message before user message
    // even though they occurred in the opposite order. The backend should return history in chronological order.
    // See: task.history array in tasks/list response has agent messages first with same timestamp as user messages.
    messages.sort((a, b) => {
      const timeDiff = a.timestamp.getTime() - b.timestamp.getTime();
      if (timeDiff !== 0) return timeDiff;
      // Same timestamp: user messages should come before assistant messages
      if (a.role === 'user' && b.role === 'assistant') return -1;
      if (a.role === 'assistant' && b.role === 'user') return 1;
      return 0;
    });

    return messages;
  }

  /**
   * Parse server date format (US format: "MM/DD/YYYY hh:mm:ss AM/PM")
   */
  private parseServerDate(dateStr?: string): Date {
    if (!dateStr) return new Date();

    try {
      // Try standard ISO format first
      const isoDate = new Date(dateStr);
      if (!isNaN(isoDate.getTime())) {
        return isoDate;
      }

      // Try US format
      return new Date(dateStr);
    } catch {
      return new Date();
    }
  }

  /**
   * Extract last message from task data
   */
  private extractLastMessage(lastTask?: Record<string, unknown>): string | undefined {
    if (!lastTask) return undefined;

    const taskStatus = lastTask.taskStatus as Record<string, unknown> | undefined;
    const message = taskStatus?.message as Record<string, unknown> | undefined;
    const parts = message?.parts as Array<{ kind: string; text?: string }> | undefined;

    if (!parts) return undefined;

    const textParts = parts
      .filter((p) => p.kind === 'text')
      .map((p) => p.text || '')
      .join('');

    return textParts || undefined;
  }
}

/**
 * Create an agent chat client
 */
export const createAgentChatClient = (config: AgentConfig): AgentChatClient => {
  return new AgentChatClient(config);
};
